/**
 * bigwaStatusPanelStore — Store unificado para feedback não-intrusivo
 *
 * Fusão de importProgressStore + notificationPanelStore em um único canal.
 * Regra: Centro = decisão humana. Canto = máquina trabalhando.
 *
 * Dois tipos de conteúdo coexistem:
 * - tasks[]: operações longas com progresso (import/export)
 * - items[]: notificações acumuláveis (padrão Paperpile)
 *
 * O painel é visível se há tasks ativas OU items pendentes.
 * Auto-dismiss items após 8s (pausa em hover). Tasks nunca auto-dismiss.
 */

import { create } from 'zustand';
import type { ButtonIntent } from '../components/BigwaToast';

// ---------------------------------------------------------------------------
// Types — Tasks (progresso de operações longas)
// ---------------------------------------------------------------------------
export type TaskMode = 'import' | 'export';

export interface StatusTask {
  id: string;
  mode: TaskMode;
  isProcessing: boolean;
  // Import fields
  currentFile: string;
  currentIndex: number;
  totalFiles: number;
  success: number;
  associated: number;
  noMetadata: string[];
  duplicatesSkipped: number;
  failures: string[];
  // Export fields
  exportTitle: string;
  exportUrl: string | null;
  exportError: string | null;
}

const INITIAL_TASK: Omit<StatusTask, 'id' | 'mode'> = {
  isProcessing: false,
  currentFile: '',
  currentIndex: 0,
  totalFiles: 0,
  success: 0,
  associated: 0,
  noMetadata: [],
  duplicatesSkipped: 0,
  failures: [],
  exportTitle: '',
  exportUrl: null,
  exportError: null,
};

// ---------------------------------------------------------------------------
// Types — Items (notificações acumuláveis)
// ---------------------------------------------------------------------------
export type NotificationStatus = 'success' | 'error' | 'pending' | 'info';

export interface NotificationAction {
  label: string;
  onClick: () => void;
  intent?: ButtonIntent;
}

export interface NotificationItem {
  id: string;
  message: string;
  status: NotificationStatus;
  timestamp: number;
  action?: NotificationAction;
  group?: string;
}

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------
interface BigwaStatusPanelState {
  // Tasks (progress)
  tasks: StatusTask[];
  // Items (notifications)
  items: NotificationItem[];
  isCollapsed: boolean;
  headerTitle: string | null;
  // Timer state (auto-dismiss items)
  _timerId: ReturnType<typeof setTimeout> | null;
  _isPaused: boolean;
  _lastResumeAt: number;
  _remainingMs: number;

  // ─── Task actions (import) ───
  startImport: (totalFiles: number, taskId?: string) => void;
  updateProgress: (currentIndex: number, currentFile: string, taskId?: string) => void;
  addSuccess: (taskId?: string) => void;
  addAssociated: (taskId?: string) => void;
  addNoMetadata: (fileName: string, taskId?: string) => void;
  addDuplicateSkipped: (taskId?: string) => void;
  addFailure: (fileName: string, taskId?: string) => void;
  finishImport: (taskId?: string) => void;

  // ─── Task actions (export) ───
  startExport: (title: string, taskId?: string) => void;
  finishExport: (url: string, taskId?: string) => void;
  failExport: (error: string, taskId?: string) => void;

  // ─── Common task actions ───
  closeTask: (taskId?: string) => void;

  // ─── Item actions (notifications) ───
  addItem: (item: Omit<NotificationItem, 'timestamp'>) => void;
  updateItem: (id: string, updates: { message?: string; status?: NotificationStatus; action?: NotificationAction | undefined }) => void;
  removeItem: (id: string) => void;
  clearItems: () => void;
  toggleCollapsed: () => void;
  dismissItems: () => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  setHeaderTitle: (title: string | null) => void;

  // ─── Panel-level ───
  /** Close everything — tasks + items */
  closeAll: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const AUTO_DISMISS_MS = 8000;
const MAX_ITEMS = 20;
const DEFAULT_TASK_ID = '__default__';

// ---------------------------------------------------------------------------
// Timer helpers
// ---------------------------------------------------------------------------
function clearExistingTimer(state: { _timerId: ReturnType<typeof setTimeout> | null }): void {
  if (state._timerId) {
    clearTimeout(state._timerId);
  }
}

function startItemTimer(set: (fn: (s: BigwaStatusPanelState) => Partial<BigwaStatusPanelState>) => void): void {
  const timerId = setTimeout(() => {
    set((s) => {
      // Don't dismiss if there are active tasks
      if (s.tasks.some(t => t.isProcessing)) {
        return {};
      }
      return {
        items: [],
        _timerId: null,
        headerTitle: null,
      };
    });
  }, AUTO_DISMISS_MS);

  set(() => ({
    _timerId: timerId,
    _isPaused: false,
    _lastResumeAt: Date.now(),
    _remainingMs: AUTO_DISMISS_MS,
  }));
}

// ---------------------------------------------------------------------------
// Task helpers
// ---------------------------------------------------------------------------
function updateTask(tasks: StatusTask[], taskId: string, updater: (t: StatusTask) => StatusTask): StatusTask[] {
  return tasks.map(t => t.id === taskId ? updater(t) : t);
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export const useBigwaStatusPanelStore = create<BigwaStatusPanelState>()((set, get) => ({
  // State
  tasks: [],
  items: [],
  isCollapsed: false,
  headerTitle: null,
  _timerId: null,
  _isPaused: false,
  _lastResumeAt: 0,
  _remainingMs: AUTO_DISMISS_MS,

  // ═══════════════════════════════════════════════════════════════
  // IMPORT TASK ACTIONS
  // ═══════════════════════════════════════════════════════════════

  startImport: (totalFiles, taskId = DEFAULT_TASK_ID) => {
    set((s) => {
      // Remove existing task with same ID (restart)
      const filtered = s.tasks.filter(t => t.id !== taskId);
      const newTask: StatusTask = {
        ...INITIAL_TASK,
        id: taskId,
        mode: 'import',
        isProcessing: true,
        totalFiles,
      };
      return { tasks: [...filtered, newTask] };
    });
  },

  updateProgress: (currentIndex, currentFile, taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        currentIndex,
        currentFile,
      })),
    }));
  },

  addSuccess: (taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        success: t.success + 1,
      })),
    }));
  },

  addAssociated: (taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        associated: t.associated + 1,
      })),
    }));
  },

  addNoMetadata: (fileName, taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        noMetadata: [...t.noMetadata, fileName],
      })),
    }));
  },

  addDuplicateSkipped: (taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        duplicatesSkipped: t.duplicatesSkipped + 1,
      })),
    }));
  },

  addFailure: (fileName, taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        failures: [...t.failures, fileName],
      })),
    }));
  },

  finishImport: (taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        isProcessing: false,
      })),
    }));
  },

  // ═══════════════════════════════════════════════════════════════
  // EXPORT TASK ACTIONS
  // ═══════════════════════════════════════════════════════════════

  startExport: (title, taskId = DEFAULT_TASK_ID) => {
    set((s) => {
      const filtered = s.tasks.filter(t => t.id !== taskId);
      const newTask: StatusTask = {
        ...INITIAL_TASK,
        id: taskId,
        mode: 'export',
        isProcessing: true,
        exportTitle: title,
      };
      return { tasks: [...filtered, newTask] };
    });
  },

  finishExport: (url, taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        isProcessing: false,
        exportUrl: url,
      })),
    }));
  },

  failExport: (error, taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: updateTask(s.tasks, taskId, (t) => ({
        ...t,
        isProcessing: false,
        exportError: error,
      })),
    }));
  },

  // ═══════════════════════════════════════════════════════════════
  // COMMON TASK ACTIONS
  // ═══════════════════════════════════════════════════════════════

  closeTask: (taskId = DEFAULT_TASK_ID) => {
    set((s) => ({
      tasks: s.tasks.filter(t => t.id !== taskId),
    }));
  },

  // ═══════════════════════════════════════════════════════════════
  // ITEM ACTIONS (notifications)
  // ═══════════════════════════════════════════════════════════════

  addItem: (item) => {
    const state = get();
    clearExistingTimer(state);

    const newItem: NotificationItem = {
      ...item,
      timestamp: Date.now(),
    };

    let newItems = [...state.items, newItem];
    if (newItems.length > MAX_ITEMS) {
      newItems = newItems.slice(newItems.length - MAX_ITEMS);
    }

    set({ items: newItems });
    startItemTimer(set);
  },

  updateItem: (id, updates) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      ),
    }));
  },

  removeItem: (id) => {
    set((state) => {
      const newItems = state.items.filter((item) => item.id !== id);
      if (newItems.length === 0 && state.tasks.length === 0) {
        clearExistingTimer(state);
        return { items: newItems, _timerId: null, headerTitle: null };
      }
      return { items: newItems };
    });
  },

  clearItems: () => {
    const state = get();
    clearExistingTimer(state);
    set({ items: [], _timerId: null, headerTitle: null });
  },

  toggleCollapsed: () => {
    set((state) => ({ isCollapsed: !state.isCollapsed }));
  },

  dismissItems: () => {
    const state = get();
    clearExistingTimer(state);
    set({ items: [], _timerId: null, headerTitle: null });
  },

  pauseTimer: () => {
    const state = get();
    if (!state._timerId || state._isPaused) return;

    clearExistingTimer(state);
    const elapsed = Date.now() - state._lastResumeAt;
    const remaining = Math.max(0, state._remainingMs - elapsed);

    set({ _timerId: null, _isPaused: true, _remainingMs: remaining });
  },

  resumeTimer: () => {
    const state = get();
    if (!state._isPaused || state.items.length === 0) return;

    const remaining = state._remainingMs;
    if (remaining <= 0) {
      // Don't auto-dismiss if tasks are active
      if (state.tasks.some(t => t.isProcessing)) {
        set({ _isPaused: false });
        return;
      }
      set({ items: [], _isPaused: false, headerTitle: null });
      return;
    }

    const timerId = setTimeout(() => {
      set((s) => {
        if (s.tasks.some(t => t.isProcessing)) return {};
        return { items: [], _timerId: null, headerTitle: null };
      });
    }, remaining);

    set({
      _timerId: timerId,
      _isPaused: false,
      _lastResumeAt: Date.now(),
      _remainingMs: remaining,
    });
  },

  setHeaderTitle: (title) => {
    set({ headerTitle: title });
  },

  // ═══════════════════════════════════════════════════════════════
  // PANEL-LEVEL
  // ═══════════════════════════════════════════════════════════════

  closeAll: () => {
    const state = get();
    clearExistingTimer(state);
    set({
      tasks: [],
      items: [],
      isCollapsed: false,
      headerTitle: null,
      _timerId: null,
      _isPaused: false,
      _remainingMs: AUTO_DISMISS_MS,
    });
  },
}));
